import {
  CONSOLE_ALIASES,
  CONSOLE_COMMANDS,
  CONSOLE_FILTER_KEYS,
  CONSOLE_LOG_TYPES,
  CONSOLE_STATUS_VALUES,
  getConsoleCommand,
  isOpenTarget,
} from './commands';
import { ConsoleActionId } from './actions';
import { ParsedConsoleCommand, SitesFilters } from './types';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 25;
const MAX_LIMIT = 100;
const DEFAULT_LOG_LINES = 100;
const MAX_LOG_LINES = 500;

function tokenize(input: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inQuotes = false;
  let quoteChar = '';

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];

    if ((ch === '"' || ch === "'") && (!inQuotes || ch === quoteChar)) {
      if (inQuotes) {
        inQuotes = false;
        quoteChar = '';
      } else {
        inQuotes = true;
        quoteChar = ch;
      }
      continue;
    }

    if (!inQuotes && /\s/.test(ch)) {
      if (current.length) {
        tokens.push(current);
        current = '';
      }
      continue;
    }

    current += ch;
  }

  if (current.length) {
    tokens.push(current);
  }

  return tokens;
}

function isNumericId(value: string): boolean {
  return /^\d+$/.test(value);
}

function parseSitesFilters(tokens: string[]): SitesFilters | { error: string } {
  const filters: SitesFilters = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
  };

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    const colonIndex = token.indexOf(':');

    if (colonIndex <= 0) {
      if (!filters.domain) {
        filters.domain = token;
        continue;
      }
      return { error: `Unexpected argument "${token}". Use key:value filters or one free-text domain.` };
    }

    const key = token.slice(0, colonIndex).toLowerCase();
    const value = token.slice(colonIndex + 1);

    if (!value) {
      return { error: `Missing value for filter "${key}".` };
    }

    if ((CONSOLE_FILTER_KEYS as readonly string[]).indexOf(key) === -1) {
      return {
        error: `Unknown filter "${key}". Supported: ${CONSOLE_FILTER_KEYS.join(', ')}.`,
      };
    }

    if (key === 'status') {
      const status = value.toUpperCase();
      if (CONSOLE_STATUS_VALUES.indexOf(status) === -1) {
        return {
          error: `Invalid status "${value}". Use ${CONSOLE_STATUS_VALUES.join(', ')}.`,
        };
      }
      filters.status = status;
    } else if (key === 'server') {
      filters.server = value;
    } else if (key === 'owner') {
      filters.owner = value;
    } else if (key === 'domain') {
      filters.domain = value;
    } else if (key === 'page') {
      if (!isNumericId(value)) {
        return { error: 'page must be a positive integer.' };
      }
      filters.page = Math.max(1, parseInt(value, 10));
    } else if (key === 'limit') {
      if (!isNumericId(value)) {
        return { error: 'limit must be a positive integer.' };
      }
      const limit = parseInt(value, 10);
      if (limit < 1 || limit > MAX_LIMIT) {
        return { error: `limit must be between 1 and ${MAX_LIMIT}.` };
      }
      filters.limit = limit;
    }
  }

  return filters;
}

function expandAliases(trimmed: string): string {
  if (!trimmed.startsWith('/')) {
    return trimmed;
  }
  const tokens = tokenize(trimmed);
  if (!tokens.length) {
    return trimmed;
  }
  const name = tokens[0].slice(1).toLowerCase();
  const alias = CONSOLE_ALIASES[name];
  if (!alias) {
    return trimmed;
  }
  const rest = tokens.slice(1);
  if (!rest.length) {
    return alias;
  }
  return `${alias} ${rest.join(' ')}`;
}

/**
 * Normalize free-text search into a /find command and expand aliases.
 */
export function normalizeConsoleInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  if (!trimmed.startsWith('/')) {
    return `/find ${trimmed}`;
  }
  return expandAliases(trimmed);
}

function missingTargetMessage(commandName: string): string {
  return `/${commandName} needs a domain/ID, or set context with /use <domain-or-id>.`;
}

function parseLogsArgs(args: string[]): ParsedConsoleCommand {
  let query: string | undefined;
  let logType = 'error';
  let lines = DEFAULT_LOG_LINES;
  const consumed = args.slice();

  if (consumed.length) {
    const first = consumed[0].toLowerCase();
    if ((CONSOLE_LOG_TYPES as readonly string[]).indexOf(first) !== -1) {
      logType = first;
      consumed.shift();
    } else if (isNumericId(consumed[0]) && consumed.length === 1) {
      // `/logs 50` → lines with context; `/logs 12345` (large) → website id
      const num = parseInt(consumed[0], 10);
      if (num <= MAX_LOG_LINES) {
        lines = Math.max(1, num);
        consumed.shift();
      } else {
        query = consumed.shift();
      }
    } else {
      query = consumed.shift();
    }
  }

  if (consumed.length) {
    const next = consumed[0].toLowerCase();
    if ((CONSOLE_LOG_TYPES as readonly string[]).indexOf(next) !== -1) {
      logType = next;
      consumed.shift();
    }
  }

  if (consumed.length) {
    if (!isNumericId(consumed[0])) {
      return { kind: 'error', message: 'lines must be a positive integer.' };
    }
    lines = parseInt(consumed[0], 10);
    consumed.shift();
  }

  if (consumed.length) {
    return { kind: 'error', message: 'Too many arguments for /logs. Usage: /logs [domain-or-id] [access|error] [lines]' };
  }

  if (lines < 1 || lines > MAX_LOG_LINES) {
    return { kind: 'error', message: `lines must be between 1 and ${MAX_LOG_LINES}.` };
  }

  return { kind: 'logs', query, logType, lines };
}

function parseActionWithSubcommand(
  commandName: string,
  args: string[],
  allowed: string[],
  actionPrefix: string,
): ParsedConsoleCommand {
  if (!args.length) {
    return {
      kind: 'error',
      message: `/${commandName} requires a subcommand: ${allowed.join('|')}.`,
    };
  }

  const sub = args[0].toLowerCase();
  if (allowed.indexOf(sub) === -1) {
    return {
      kind: 'error',
      message: `Unknown /${commandName} subcommand "${args[0]}". Use: ${allowed.join('|')}.`,
    };
  }

  if (args.length > 2) {
    return {
      kind: 'error',
      message: `/${commandName} ${sub} accepts at most one domain or ID.`,
    };
  }

  return {
    kind: 'action',
    actionId: `${actionPrefix}.${sub}` as ConsoleActionId,
    query: args[1],
  };
}

function parseSimpleAction(
  actionId: ConsoleActionId,
  args: string[],
  commandLabel: string,
): ParsedConsoleCommand {
  if (args.length > 1) {
    return {
      kind: 'error',
      message: `/${commandLabel} accepts at most one domain or ID.`,
    };
  }
  return {
    kind: 'action',
    actionId,
    query: args[0],
  };
}

function parseManageAction(
  commandName: string,
  args: string[],
  actionId: ConsoleActionId,
): ParsedConsoleCommand {
  if (!args.length || args[0].toLowerCase() !== 'manage') {
    return {
      kind: 'error',
      message: `Usage: /${commandName} manage [domain-or-id]`,
    };
  }
  if (args.length > 2) {
    return {
      kind: 'error',
      message: `/${commandName} manage accepts at most one domain or ID.`,
    };
  }
  return {
    kind: 'action',
    actionId,
    query: args[1],
  };
}

function parseRetryArgs(args: string[]): ParsedConsoleCommand {
  if (!args.length) {
    return { kind: 'action', actionId: 'website.retry' };
  }

  // /retry <creationId> <actionId> [query?]
  if (args.length >= 2 && isNumericId(args[0]) && isNumericId(args[1])) {
    if (args.length > 3) {
      return {
        kind: 'error',
        message: 'Usage: /retry [creation-id] [action-id] [domain-or-id]',
      };
    }
    return {
      kind: 'action',
      actionId: 'website.retry',
      args: [args[0], args[1]],
      query: args[2],
    };
  }

  if (args.length === 1) {
    return {
      kind: 'action',
      actionId: 'website.retry',
      query: args[0],
    };
  }

  return {
    kind: 'error',
    message: 'Usage: /retry [creation-id] [action-id] [domain-or-id]',
  };
}

function parseOpenArgs(args: string[]): ParsedConsoleCommand {
  if (!args.length) {
    return { kind: 'open' };
  }

  const first = args[0].toLowerCase();
  if (isOpenTarget(first)) {
    if (args.length > 2) {
      return {
        kind: 'error',
        message: `Usage: /open ${first} [domain-or-id]`,
      };
    }
    return {
      kind: 'open',
      target: first,
      query: args[1],
    };
  }

  if (args.length > 1) {
    return {
      kind: 'error',
      message: 'Usage: /open [domain-or-id|server|group|unix] [domain-or-id]',
    };
  }

  return { kind: 'open', target: 'website', query: args[0] };
}

function parseDnsOrSsl(commandName: 'dns' | 'ssl', args: string[]): ParsedConsoleCommand {
  if (!args.length) {
    return { kind: commandName };
  }

  if (args[0].toLowerCase() === 'manage') {
    if (args.length > 2) {
      return {
        kind: 'error',
        message: `/${commandName} manage accepts at most one domain or ID.`,
      };
    }
    return {
      kind: 'action',
      actionId: `${commandName}.manage` as ConsoleActionId,
      query: args[1],
    };
  }

  if (args.length > 1) {
    return {
      kind: 'error',
      message: `/${commandName} accepts at most one domain or ID (or "manage").`,
    };
  }

  return { kind: commandName, query: args[0] };
}

export function parseConsoleInput(raw: string): ParsedConsoleCommand {
  const normalized = normalizeConsoleInput(raw);
  if (!normalized) {
    return { kind: 'error', message: 'Enter a command or search query. Try /help.' };
  }

  const tokens = tokenize(normalized);
  if (!tokens.length) {
    return { kind: 'error', message: 'Enter a command or search query. Try /help.' };
  }

  const commandToken = tokens[0];
  if (!commandToken.startsWith('/')) {
    return { kind: 'error', message: 'Commands must start with /. Try /help.' };
  }

  const commandName = commandToken.slice(1).toLowerCase();
  const definition = getConsoleCommand(commandName);
  if (!definition) {
    const known = CONSOLE_COMMANDS
      .filter((c) => !c.aliasOf)
      .map((c) => `/${c.name}`)
      .join(', ');
    return { kind: 'error', message: `Unknown command "/${commandName}". Known commands: ${known}.` };
  }

  const args = tokens.slice(1);

  switch (definition.name) {
    case 'help':
      return { kind: 'help' };
    case 'clear':
      return { kind: 'clear' };
    case 'history':
      return { kind: 'history' };
    case 'rerun':
      return { kind: 'rerun' };
    case 'errors':
    case 'disabled':
    case 'pending':
      // Should already be expanded by normalize; treat as sites if reached.
      return parseConsoleInput(definition.aliasOf || `/sites status:${definition.name.toUpperCase()}`);
    case 'find': {
      if (!args.length) {
        return {
          kind: 'error',
          message: `/${definition.name} requires a domain or numeric ID. Example: ${definition.examples[0]}`,
        };
      }
      if (args.length > 1) {
        return {
          kind: 'error',
          message: `/${definition.name} accepts a single domain or ID. Quote values with spaces.`,
        };
      }
      return { kind: 'find', query: args[0] };
    }
    case 'use': {
      if (!args.length) {
        return { kind: 'use' };
      }
      if (args.length === 1 && args[0].toLowerCase() === 'clear') {
        return { kind: 'use', clear: true };
      }
      if (args.length > 1) {
        return { kind: 'error', message: 'Usage: /use [domain-or-id|clear]' };
      }
      return { kind: 'use', query: args[0] };
    }
    case 'site':
    case 'activity':
    case 'health': {
      if (args.length > 1) {
        return {
          kind: 'error',
          message: `/${definition.name} accepts at most one domain or ID.`,
        };
      }
      return { kind: definition.name, query: args[0] } as ParsedConsoleCommand;
    }
    case 'open':
      return parseOpenArgs(args);
    case 'dns':
      return parseDnsOrSsl('dns', args);
    case 'ssl':
      return parseDnsOrSsl('ssl', args);
    case 'logs':
      return parseLogsArgs(args);
    case 'sites': {
      const parsed = parseSitesFilters(args);
      if ('error' in parsed) {
        return { kind: 'error', message: parsed.error };
      }
      return { kind: 'sites', filters: parsed };
    }
    case 'actions': {
      if (args.length > 1) {
        return { kind: 'error', message: '/actions accepts at most one domain or ID.' };
      }
      return { kind: 'actions', query: args[0] };
    }
    case 'staging':
      return parseActionWithSubcommand('staging', args, ['create', 'merge'], 'staging');
    case 'alias':
      return parseActionWithSubcommand('alias', args, ['create'], 'alias');
    case 'subdomain':
      return parseActionWithSubcommand('subdomain', args, ['create'], 'subdomain');
    case 'wp':
      return parseActionWithSubcommand('wp', args, ['install', 'uninstall'], 'wp');
    case 'enable':
      return parseSimpleAction('website.enable', args, 'enable');
    case 'disable':
      return parseSimpleAction('website.disable', args, 'disable');
    case 'rename':
      return parseSimpleAction('website.rename', args, 'rename');
    case 'delete':
      return parseSimpleAction('website.delete', args, 'delete');
    case 'recreate':
      return parseSimpleAction('website.recreate', args, 'recreate');
    case 'retry':
      return parseRetryArgs(args);
    case 'db':
      return parseManageAction('db', args, 'db.manage');
    case 'mail':
      return parseManageAction('mail', args, 'mail.manage');
    case 'ftp':
      return parseManageAction('ftp', args, 'ftp.manage');
    case 'vhost':
      return parseManageAction('vhost', args, 'vhost.manage');
    case 'redis':
      return parseManageAction('redis', args, 'redis.manage');
    default:
      return { kind: 'error', message: `Unhandled command "/${commandName}".` };
  }
}

export function sitesFiltersToLabel(filters: SitesFilters): string {
  const parts: string[] = [];
  if (filters.domain) {
    parts.push(`domain:${filters.domain}`);
  }
  if (filters.status) {
    parts.push(`status:${filters.status}`);
  }
  if (filters.server) {
    parts.push(`server:${filters.server}`);
  }
  if (filters.owner) {
    parts.push(`owner:${filters.owner}`);
  }
  parts.push(`page:${filters.page}`);
  parts.push(`limit:${filters.limit}`);
  return parts.join(' ');
}

export function requireTarget(
  query: string | undefined,
  contextId: number | undefined,
  commandName: string,
): { query: string } | { error: string } {
  if (query) {
    return { query };
  }
  if (contextId !== undefined) {
    return { query: String(contextId) };
  }
  return { error: missingTargetMessage(commandName) };
}

export { isNumericId, tokenize, DEFAULT_PAGE, DEFAULT_LIMIT };
